import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Modal, Space, Table, Typography } from 'antd'
import { get } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import { PermissionGuard } from '../../../common/components/routes'
import { HistoryLink } from '../../../common/components/sections'
import {
  StyledPageActionButton,
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
  StyledVerticalSpace,
} from '../../../common/components/styled'
import {
  APP_ROUTES,
  EVENT_METADATA_TYPES,
  PERMISSIONS,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useDocumentTitle, useIngredientsQuery } from '../../../common/hooks'

import { INGREDIENT_SPECIAL_CASE_TYPE_LABELS } from '../../constants'
import { renderIngredientSpecialCaseEvent } from '../../helpers'
import {
  useDeleteIngredientSpecialCaseMutation,
  useIngredientSpecialCasesQuery,
} from '../../hooks'
import { IngredientSpecialCasesTableActionColumn } from '../sections'

const IngredientSpecialCasesPage = () => {
  useDocumentTitle(
    APP_ROUTES.INGREDIENTS.subMenuItems.INGREDIENT_SPECIAL_CASES.name,
  )

  const {
    data,
    error: fetchError,
    isLoading,
    latestEvent,
  } = useIngredientSpecialCasesQuery()

  const [deleteIngredientSpecialCase, { deleteError, idToDelete }] =
    useDeleteIngredientSpecialCaseMutation()

  const variables = useMemo(() => ({ disableNodes: () => false }), [])

  const {
    groupedById: ingredientsGroupedById,
    isLoading: isLoadingIngredients,
    error: ingredientsError,
  } = useIngredientsQuery(variables)

  const [showModal, setShowModal] = useState(false)
  const [modalContent, setModalContent] = useState(null)

  const renderEvent = useCallback(
    event =>
      renderIngredientSpecialCaseEvent(event, ingredientsGroupedById, text => {
        setShowModal(true)
        setModalContent(text)
      }),
    [ingredientsGroupedById],
  )

  const tableColumns = useMemo(
    () => [
      {
        title: 'Ingredient Special Case ID',
        dataIndex: 'id',
      },
      {
        title: 'Name',
        dataIndex: 'name',
      },
      {
        title: 'Type',
        dataIndex: 'type',
        render: type => get(INGREDIENT_SPECIAL_CASE_TYPE_LABELS, type),
      },
      {
        align: 'right',
        render: ingredientSpecialCase => (
          <IngredientSpecialCasesTableActionColumn
            onDelete={deleteIngredientSpecialCase}
            ingredientSpecialCase={ingredientSpecialCase}
          />
        ),
      },
    ],
    [deleteIngredientSpecialCase],
  )

  useEffect(() => {
    if (fetchError || deleteError || ingredientsError) {
      showErrorNotification({
        title: 'Ingredient Special Case error',
        description: fetchError || deleteError || ingredientsError,
      })
    }
  }, [deleteError, fetchError, idToDelete, ingredientsError])

  return (
    <>
      <StyledPageHeader>
        <StyledPageTitle>Ingredient Special Cases</StyledPageTitle>
        <StyledPageActions>
          <StyledVerticalSpace>
            <Space>
              <UpdateDataText
                updatedAt={latestEvent?.createdAt}
                updatedBy={latestEvent?.createdBy?.name}
              />

              <HistoryLink
                filterEventTypes
                eventMetadataTypes={[
                  EVENT_METADATA_TYPES.INGREDIENT_SPECIAL_CASE,
                ]}
                renderEvent={renderEvent}
              />
            </Space>
            <PermissionGuard
              permissions={PERMISSIONS.INGREDIENT_SPECIAL_CASE.CREATE}
            >
              <StyledPageActionButton>
                <Link to={APP_ROUTES.INGREDIENT_SPECIAL_CASE_CREATE.link}>
                  Add Ingredient special case
                </Link>
              </StyledPageActionButton>
            </PermissionGuard>
          </StyledVerticalSpace>
        </StyledPageActions>
      </StyledPageHeader>

      <Table
        loading={isLoading || isLoadingIngredients}
        rowKey="id"
        columns={tableColumns}
        dataSource={data}
        pagination={false}
      />

      <Modal
        title="Event Details"
        visible={showModal}
        onCancel={() => {
          setShowModal(false)
          setModalContent(null)
        }}
        footer={null}
      >
        <Typography.Text>{modalContent}</Typography.Text>
      </Modal>
    </>
  )
}

export default IngredientSpecialCasesPage
